import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import isEmail from 'validator/es/lib/isEmail';
import isMobilePhone from 'validator/es/lib/isMobilePhone';
import * as Yup from 'yup';
import { Button, FieldWrapper, FormError, Identity, Input, Modal, ModalAvatar, ModalFooter, ModalForm, ModalFormLayout, ModalHeader, ModalText, ModalTitle, Select } from 'components';
import { ICreatableSelectOption } from 'components/CreatableSelect/CreatableSelect';
import { JobTitleCreatableSelect } from 'components/JobTitleCreatableSelect/JobTitleCreatableSelect';
import { AdminRoleOptions, DDOption, DisbursementOptions, DismissReasons, MOBILE_LOCALE, UserRoleOptions } from 'config/const';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import validations from 'lib/Validations';
import { routeConstants } from 'routes';
import { PermissionGate } from 'screens/Security';
import { clearRequest, selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { addEmployeeToSiteRequest, deleteEmployeeRequest, Employee, resendInvitationRequest, selectEmployee, updateEmployeeRequest } from 'store/Employee';
import { selectCurrentEmployeeManagedSites, selectCurrentEmployeeOrg } from 'store/Employers';
import { selectPayoutStatsByStatus } from 'store/Stats';
import { IJobTitleState, IStore } from 'store/types';
import { selectCurrentUser } from 'store/User';
import { EmployeeActionRequiredType, EmployeeRole, EmployeeStatus, EmployeeType, IApiErrorPayloadWithCode, IEmployee, IEmployerSite, PayoutStatus } from 'types';
import { getDefaultDisbursementMethod, isEmployeeIdRequired, isSiteRole } from 'utils';
import { getPayPeriodClosedMessage } from 'utils/errors';
import { formatMobile, stripCountryCode, stripSpaces } from 'utils/strings';
import AlertHeader from '../components/Alert/AlertHeader';
import { PayPeriodClosedModal } from './PayPeriodClosedModal';
import { HeaderContent, InvitationNotice, RemoveButton, ReviewNowButton } from './styles';

const StyledAlertModalHeader = styled(ModalHeader)`
  padding-bottom: ${toRem(theme.space.xl)};
`;

interface EditUserProps {
  isActive: boolean;
  onClose: () => void;
  showReviewModal: () => void;
}

interface EditEmployeeForm {
  firstName: string;
  lastName: string;
  inviteRecipient?: string;
  role?: DDOption;
  site?: DDOption;
  jobTitle?: ICreatableSelectOption<IJobTitleState>;
  disbursementMethod?: DDOption;
  employeeId?: string;
}

const EditUser: React.FC<EditUserProps> = ({ isActive, onClose, showReviewModal }) => {
  const history = useHistory();
  const [currentStep, setCurrentStep] = useState<'initial' | 'paymentsAlert' | 'dismiss' | 'delete'>('initial');
  const { id } = useParams() as any;
  const currentUser = useSelector(selectCurrentUser());
  const dispatch = useDispatch();
  const currentOrganizationId = useSelector(selectCurrentEmployee())?.organizationId;
  const currentOrganization = useSelector(selectCurrentEmployeeOrg);
  const sites = useSelector(selectCurrentEmployeeManagedSites);

  const orgSites =
    sites?.map((site: IEmployerSite) => ({
      label: site.name,
      value: site.id,
    })) || [];

  // User/Employee Meta
  const employeeDetail = useSelector(selectEmployee());
  const employeeName = employeeDetail?.name || '';
  const employeeAvatar = employeeDetail && employeeDetail.user?.avatarUrl;
  const employeeHasPaymentMethod = employeeDetail && !!employeeDetail.user?.card;
  const [duplicateEmployee, setDuplicateEmployee] = useState<IEmployee>();

  // User/Employee Status
  const isApproved = employeeDetail?.status === EmployeeStatus.APPROVED;
  const isPending = employeeDetail?.status === EmployeeStatus.INVITED;
  const isDismissed = employeeDetail?.status === EmployeeStatus.DISMISSED;
  const isExpired = employeeDetail?.status === EmployeeStatus.ACTION_REQUIRED && [EmployeeActionRequiredType.INVITE_EXPIRED, EmployeeActionRequiredType.INVITE_REQUIRED].includes(employeeDetail.actionRequired);
  const inviteFailure = employeeDetail?.status === EmployeeStatus.ACTION_REQUIRED && employeeDetail?.actionRequired === EmployeeActionRequiredType.INVITE_SEND_FAILURE;
  const isPendingReview = employeeDetail?.actionRequired === EmployeeActionRequiredType.EMPLOYER_APPROVAL && employeeDetail?.status === EmployeeStatus.ACTION_REQUIRED;

  const shouldDisplayInviteMethod = isPending || isExpired || inviteFailure;
  const shouldDisplayRoleSelector = employeeDetail?.type === EmployeeType.ADMIN && employeeDetail?.user?.id !== currentUser?.id;
  const currentUserCanDeleteEmployee = currentOrganization?.type === 'admin' || employeeDetail?.type === 'user';

  const validationSchema = Yup.object().shape({
    firstName: validations.firstNameValidationSchema,
    lastName: validations.lastNameValidationSchema,
    ...(shouldDisplayInviteMethod && {
      inviteRecipient: Yup.string().test('IsIdentity', 'Invalid email address or mobile number', (identity: string) => !isEmpty(identity) && (isEmail(identity || '') || isMobilePhone(identity || '', MOBILE_LOCALE))),
    }),
    ...validations.employeeSiteValidationSchema(sites),
  });

  const resendInvite = (inviteRecipient?: string) => {
    if (inviteRecipient) {
      setDuplicateEmployee(undefined);
      dispatch(
        resendInvitationRequest(employeeDetail?.id, stripCountryCode(inviteRecipient), {
          onDuplicate: (dupEmp: IEmployee) => setDuplicateEmployee(dupEmp),
        })
      );
    }
  };

  const { errors, touched, isValid, dirty, values, handleSubmit, getFieldProps, handleBlur, setFieldValue, setFieldTouched } = useFormik({
    initialValues: {
      firstName: employeeDetail?.firstName || '',
      lastName: employeeDetail?.lastName || '',
      role: { ...AdminRoleOptions[0] },
      inviteRecipient: employeeDetail?.inviteRecipient || '',
      disbursementMethod: { label: '', value: '' },
      site: {
        label: '',
        value: '',
      },
      jobTitle: {
        label: '',
        value: {
          title: '',
        } as IJobTitleState,
      },
    },
    onSubmit: (values: EditEmployeeForm) => {
      const payload = {
        firstName: stripSpaces(values.firstName),
        lastName: stripSpaces(values.lastName),
        ...(shouldDisplayInviteMethod &&
          values.inviteRecipient &&
          values.inviteRecipient !== employeeDetail?.inviteRecipient && {
            inviteRecipient: stripCountryCode(values.inviteRecipient),
          }),
        organizationId: currentOrganizationId,
        ...(isSiteRole(values.role?.value as EmployeeRole) && { type: EmployeeType.USER }),
      };

      // Set site payload when converting org admin to a user.
      let sitePayload: any;
      if (isSiteRole(values.role?.value as EmployeeRole)) {
        sitePayload = {
          role: values.role?.value === EmployeeRole.SITE_ADMIN ? EmployeeRole.ADMIN : values.role?.value,
          id: values.site?.value,
          ...(!isEmpty(values.employeeId) ? { employeeId: values.employeeId } : {}),
          job: { title: values.jobTitle?.value?.title },
          disbursementMethod: values.disbursementMethod?.value,
        };
      }

      setDuplicateEmployee(undefined);
      const meta = !sitePayload
        ? {
            successMessage: 'Successfully updated employee',
          }
        : {
            onSuccess: () => dispatch(addEmployeeToSiteRequest(employeeDetail?.id, sitePayload, { successMessage: 'Successfully updated employee' })),
          };

      // Update the employee record
      dispatch(updateEmployeeRequest(id, payload, { ...meta, onDuplicate: (dupEmp: IEmployee) => setDuplicateEmployee(dupEmp) }));
    },
    validateOnBlur: true,
    validationSchema,
    enableReinitialize: true,
  });

  const dismissFormik = useFormik({
    initialValues: {
      dismissReason: '',
    },
    validationSchema: Yup.object().shape({
      dismissReason: Yup.string().required('Required'),
    }),
    onSubmit: (values) => {
      const payload = {
        status: EmployeeStatus.DISMISSED,
        dismissReason: values.dismissReason,
      };
      dispatch(updateEmployeeRequest(id, payload, { successMessage: 'Successfully dismissed employee' }));
    },
  });

  const dismissError = dismissFormik.touched.dismissReason && dismissFormik.errors.dismissReason;
  const apiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Employee.UPDATE_EMPLOYEE_REQUEST)) as IApiErrorPayloadWithCode;
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.UPDATE_EMPLOYEE_REQUEST));

  const isDeleteLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.DELETE_EMPLOYEE_REQUEST));
  const deleteApiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Employee.DELETE_EMPLOYEE_REQUEST));

  // account_services refuses a dismiss or delete while the employee has payments in a closed pay period.
  const dismissBlockedMessage = currentStep === 'dismiss' && getPayPeriodClosedMessage(apiErrors);
  const deleteBlockedMessage = currentStep === 'delete' && getPayPeriodClosedMessage(deleteApiErrors);

  const outstandingPaymentsCount = useSelector(selectPayoutStatsByStatus())?.reduce((count, stat) => ([PayoutStatus.PENDING, PayoutStatus.EXPIRED].includes(stat.status) ? count + stat.count : count), 0);

  const validateField = (field: 'firstName' | 'lastName' | 'inviteRecipient' | 'role' | 'site' | 'jobTitle' | 'disbursementMethod' | 'employeeId') => Boolean(touched[field] && errors[field]);

  useEffect(() => {
    dispatch(clearRequest(Employee.UPDATE_EMPLOYEE_REQUEST));
    dispatch(clearRequest(Employee.DELETE_EMPLOYEE_REQUEST));
  }, []);

  const cancelDismissal = () => {
    dismissFormik.resetForm();
    setCurrentStep('initial');
  };

  const handleDelete = () => dispatch(deleteEmployeeRequest(id, () => history.push(routeConstants.private.employees)));

  if (dismissBlockedMessage) return <PayPeriodClosedModal isActive={isActive} verb="dismiss" message={dismissBlockedMessage} onClose={onClose} />;
  if (deleteBlockedMessage) return <PayPeriodClosedModal isActive={isActive} verb="delete" message={deleteBlockedMessage} onClose={onClose} />;

  return (
    <>
      {currentStep === 'initial' && (
        <Modal maxWidth={540} isActive={isActive} onClose={onClose}>
          <ModalHeader>
            <ModalAvatar image={employeeAvatar} username={employeeName} hasPaymentMethod={employeeHasPaymentMethod} />
            <HeaderContent>
              <ModalTitle>{employeeName}</ModalTitle>
            </HeaderContent>
          </ModalHeader>

          {shouldDisplayInviteMethod && (
            <InvitationNotice
              dataCy="invitation-notice"
              appearance={Appearance.WARNING}
              message={
                inviteFailure
                  ? `There was an error sending invite to ${isMobilePhone(employeeDetail.inviteRecipient, MOBILE_LOCALE) ? formatMobile(employeeDetail.inviteRecipient) : employeeDetail.inviteRecipient}`
                  : `Invitation to ${currentOrganization?.name} is ${isPending ? 'pending' : 'expired'}.`
              }
            />
          )}

          {isPendingReview ? (
            <InvitationNotice
              appearance={Appearance.WARNING}
              message={
                <>
                  <p>User is pending review.</p>
                  <ReviewNowButton label="Review Now" onClick={showReviewModal} />
                </>
              }
            />
          ) : null}

          <ModalForm onSubmit={handleSubmit}>
            <ModalFormLayout disabled={isDismissed}>
              <FieldWrapper label="First Name" message={validateField('firstName') ? errors.firstName : ''} hasError={validateField('firstName')}>
                <Input id="firstName" type="text" disabled={isLoading || isDismissed} data-cy="first-name-input" {...getFieldProps('firstName')} />
              </FieldWrapper>

              <FieldWrapper label="Last Name" message={validateField('lastName') ? errors.lastName : ''} hasError={validateField('lastName')}>
                <Input id="lastName" type="text" disabled={isLoading || isDismissed} data-cy="last-name-input" {...getFieldProps('lastName')} />
              </FieldWrapper>

              {shouldDisplayInviteMethod && (
                <FieldWrapper
                  label="Invite Method"
                  message={validateField('inviteRecipient') ? errors.inviteRecipient : ''}
                  hasError={validateField('inviteRecipient')}
                  action={employeeDetail?.sites?.length > 0 && !errors.inviteRecipient && !isLoading ? 'Resend Invitation' : ''}
                  onAction={() => resendInvite(values.inviteRecipient)}
                >
                  <Identity
                    id="inviteRecipient"
                    value={values.inviteRecipient}
                    disabled={isLoading}
                    onBlur={handleBlur}
                    onAccept={(val: any) => {
                      setFieldValue('inviteRecipient', val);
                    }}
                    type="text"
                    placeholder="Email or Mobile Number"
                  />
                </FieldWrapper>
              )}

              {shouldDisplayRoleSelector && (
                <PermissionGate requiredRoles={[EmployeeRole.ADMIN]}>
                  <FieldWrapper data-cy="role-field" label="Role" hasError={validateField('role')}>
                    <Select
                      data-cy="role-input"
                      disabled={isLoading || isDismissed}
                      placeholder="Select Role"
                      options={UserRoleOptions}
                      onChange={(selected) => {
                        setFieldValue('role', selected?.[0]);
                      }}
                      values={[values.role]}
                    />
                  </FieldWrapper>

                  {isSiteRole(values.role?.value as EmployeeRole) && (
                    <>
                      <FieldWrapper
                        label="Employee ID"
                        data-cy="employee-id-field"
                        optional={!isEmployeeIdRequired(values.site?.value, sites)}
                        message={validateField('employeeId') ? errors.employeeId : ''}
                        hasError={validateField('employeeId')}
                      >
                        <Input data-cy="employee-id-input" {...getFieldProps('employeeId')} id="employeeId" type="text" placeholder="Enter an Employee ID" maxLength={50} />
                      </FieldWrapper>
                      {!isEmpty(orgSites) && (
                        <FieldWrapper data-cy="site-field" label="Site" message={validateField('site') ? (errors.site?.value as string) : ''} hasError={validateField('site')}>
                          <Select
                            data-cy="site-input"
                            disabled={isLoading || isDismissed}
                            placeholder="Select Site"
                            options={orgSites}
                            onChange={(selected) => {
                              const site = selected?.[0];
                              setFieldValue('site', site);
                              setFieldValue('disbursementMethod', getDefaultDisbursementMethod(site?.value, sites), false);
                            }}
                            values={[values.site]}
                          />
                        </FieldWrapper>
                      )}
                      {!isEmpty(values.site) && (
                        <FieldWrapper data-cy="job-title-field" label="Job Title" message={validateField('jobTitle') ? (errors.jobTitle?.value?.title as string) : ''} hasError={validateField('jobTitle')}>
                          <JobTitleCreatableSelect
                            dataCy="job-title-selector"
                            createNewLabel="Add {search} as a new job title"
                            disabled={!values.site?.value || isLoading || isDismissed}
                            placeholder="Select Job Title"
                            siteId={values.site.value}
                            onChange={(selected) => {
                              setFieldValue('jobTitle', selected);
                            }}
                            onInputBlur={() => setFieldTouched('jobTitle', true)}
                          />
                        </FieldWrapper>
                      )}
                      <FieldWrapper data-cy="disbursement-field" label="Disbursement Method" hasError={validateField('disbursementMethod')}>
                        <Select
                          data-cy="disbursement-input"
                          disabled={isLoading}
                          placeholder="Select Disbursement Method"
                          options={DisbursementOptions}
                          onChange={(selected) => {
                            setFieldValue('disbursementMethod', selected?.[0]);
                          }}
                          values={[values.disbursementMethod]}
                        />
                      </FieldWrapper>
                    </>
                  )}
                </PermissionGate>
              )}

              {apiErrors?.errorMessages &&
                (duplicateEmployee ? (
                  <FormError data-cy="duplicate-employee-error">{`${duplicateEmployee.inviteRecipient} is being used by ${duplicateEmployee.name}.`}</FormError>
                ) : (
                  apiErrors.errorMessages.map((error, index) => <FormError key={`error-message-${index}`}>{error}</FormError>)
                ))}
              {isApproved && currentUser?.id !== employeeDetail.user?.id && employeeDetail.sites.some((site) => site.status !== EmployeeStatus.DISMISSED) && (
                <PermissionGate requiredRoles={[EmployeeRole.ADMIN]}>
                  <RemoveButton
                    fontWeight={theme.font.weight.semiBold}
                    fontSize={11}
                    label="Dismiss From All Sites"
                    data-cy="dismiss-from-all-sites-button"
                    onClick={() => {
                      if (outstandingPaymentsCount) {
                        setCurrentStep('paymentsAlert');
                      } else {
                        setCurrentStep('dismiss');
                      }
                    }}
                  />
                </PermissionGate>
              )}
              {(isPendingReview || isExpired || employeeDetail?.status === EmployeeStatus.INVITED || employeeDetail?.status === EmployeeStatus.DISMISSED) && currentUserCanDeleteEmployee && (
                <RemoveButton
                  fontWeight={theme.font.weight.semiBold}
                  fontSize={11}
                  label="Delete user"
                  data-cy="delete-user-button"
                  onClick={() => {
                    if (outstandingPaymentsCount) {
                      setCurrentStep('paymentsAlert');
                    } else {
                      setCurrentStep('delete');
                    }
                  }}
                />
              )}
            </ModalFormLayout>

            <ModalFooter columns={2}>
              <Button label="Close" appearance="white" size="large" onClick={onClose} />
              <Button label="Update" disabled={!(isValid && dirty) || isDismissed} isProcessing={isLoading} type="submit" appearance="primary" size="large" />
            </ModalFooter>
          </ModalForm>
        </Modal>
      )}
      {currentStep === 'dismiss' && (
        <Modal maxWidth={540} isActive={isActive} onClose={onClose}>
          <StyledAlertModalHeader>
            <ModalAvatar image={employeeAvatar} username={employeeName} hasPaymentMethod={employeeHasPaymentMethod} />
            <HeaderContent>
              <ModalTitle>Dismiss User From All Sites</ModalTitle>
              <ModalText>Dismissed users will no longer be able to receive payments at the selected site. You can reactivate or delete this employee later.</ModalText>
            </HeaderContent>
          </StyledAlertModalHeader>

          <ModalForm onSubmit={dismissFormik.handleSubmit}>
            <ModalFormLayout>
              <FieldWrapper label="Reason for Dismissal" message={dismissError || ''} hasError={Boolean(dismissError)}>
                <Select placeholder="Choose a reason" options={DismissReasons} values={[]} onChange={(selected) => dismissFormik.setFieldValue('dismissReason', selected?.[0]?.value)} data-cy="dismiss-reason-input" />
              </FieldWrapper>
              {apiErrors?.errorMessages && apiErrors?.errorMessages.map((error, index) => <FormError key={`error-message-${index}`}>{error}</FormError>)}
            </ModalFormLayout>

            <ModalFooter columns={2} centerButtons>
              <Button label="Cancel" appearance="white" size="large" onClick={cancelDismissal} />
              <Button label="Dismiss" disabled={!dismissFormik.dirty} isProcessing={isLoading} type="submit" appearance="destructive" size="large" data-cy="dismiss-user-button" />
            </ModalFooter>
          </ModalForm>
        </Modal>
      )}
      {currentStep === 'paymentsAlert' && (
        <Modal maxWidth={420} isActive={isActive} onClose={onClose}>
          <ModalHeader>
            <ModalTitle>
              <AlertHeader title="User Has Outstanding Payments" />
            </ModalTitle>
            <HeaderContent>
              <ModalText>
                Users cannot be {isApproved ? 'dismissed' : 'removed'} while payments are pending or expired. Would you like to cancel all outstanding payments and {isApproved ? 'dismiss' : 'remove'} this user now?
              </ModalText>
            </HeaderContent>
          </ModalHeader>

          <ModalFooter columns={1}>
            <Button
              label={`Cancel Payments And ${isApproved ? 'Dismiss' : 'Remove'} User`}
              onClick={() => {
                setCurrentStep(isApproved ? 'dismiss' : 'delete');
              }}
              appearance="primary"
              size="large"
              data-cy="cancel-payments-button"
            />
          </ModalFooter>
        </Modal>
      )}
      {currentStep === 'delete' && (
        <Modal isActive={isActive} onClose={onClose}>
          <ModalHeader style={{ paddingBottom: '0' }}>
            <HeaderContent>
              <AlertHeader icon="Caution" iconFill={theme.color.warning[50]} title="Delete User?" />
              <ModalText>Deleted users will no longer be able to receive payments. Any existing pending or expired payments will be canceled.</ModalText>
              <ModalText>Are you sure you would like to delete this user?</ModalText>
            </HeaderContent>
          </ModalHeader>
          <ModalForm>
            <ModalFooter columns={2} centerButtons>
              <Button label="Cancel" appearance="white" size="large" onClick={cancelDismissal} />
              <Button label="Delete" disabled={false} isProcessing={isDeleteLoading} type="button" appearance="destructive" size="large" onClick={handleDelete} />
            </ModalFooter>
          </ModalForm>
        </Modal>
      )}
    </>
  );
};

export default EditUser;
